import { useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiContentCopy, mdiPlay, mdiPlus, mdiSend, mdiSitemapOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { EmptyState } from '../ui/empty-state'
import { PageHeader } from '../shared/PageHeader'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import {
    formatSeconds,
    labelIn,
    useCloneScenario,
    useProposeScenario,
    useScenarios,
    useStartBacklog,
    useUpdateScenario,
    type RemediationScenario,
} from '../../hooks/useScenariosApi'

type Trigger = { subtypes?: string[]; rule_codes?: string[]; rule_classes?: string[]; target_asset_types?: string[]; auto_start?: boolean }

/** The catalogue of remediation scenarios (spec 2026-10-28-15 § 10). */
export function ScenariosListPage() {
    const { org } = useParams({ strict: false }) as { org: string }
    const { t, i18n } = useTranslation('scenarios')
    const navigate = useNavigate()
    const canWrite = useCan('compliance.write')
    const [q, setQ] = useState('')
    const [origin, setOrigin] = useState('')
    const [universe, setUniverse] = useState('')
    const { data: list = [], isLoading } = useScenarios(org, { q, origin, universe })
    const update = useUpdateScenario(org)
    const clone = useCloneScenario(org)
    const propose = useProposeScenario(org)
    const backlog = useStartBacklog(org)
    const [notice, setNotice] = useState<string | null>(null)

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('common:nav.settings'), path: `/${org}/settings` },
        { title: t('title') },
    ])

    const universes = Array.from(new Set(list.flatMap((s) => s.universes ?? []))).sort()

    const triggerSummary = (s: RemediationScenario) => {
        const tr = (s.trigger ?? {}) as Trigger
        const parts = [...(tr.subtypes ?? []), ...(tr.rule_codes ?? []), ...(tr.rule_classes ?? [])]
        if (parts.length === 0) return t('list.no_trigger')
        const head = parts.slice(0, 3).join(', ') + (parts.length > 3 ? ` +${parts.length - 3}` : '')
        return tr.auto_start ? `${head} · ${t('list.auto')}` : head
    }

    return (
        <div data-testid="scenarios-page">
            <PageHeader
                className="mb-5"
                icon={mdiSitemapOutline}
                title={t('title')}
                description={t('subtitle')}
                actions={canWrite ? (
                    <Button onClick={() => navigate({ to: '/$org/remediation-scenarios/$code', params: { org, code: 'new' } })} data-testid="scenario-new">
                        <AppIcon path={mdiPlus} size="sm" />
                        {t('actions.new')}
                    </Button>
                ) : undefined}
            />
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('filters.q')} className="max-w-xs" data-testid="scenarios-filter-q" />
                <select className="h-9 rounded border border-border bg-background px-2 text-sm" value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="scenarios-filter-origin">
                    <option value="">{t('filters.origin_all')}</option>
                    <option value="shipped">{t('origin.shipped')}</option>
                    <option value="organization">{t('origin.organization')}</option>
                </select>
                <select className="h-9 rounded border border-border bg-background px-2 text-sm" value={universe} onChange={(e) => setUniverse(e.target.value)} data-testid="scenarios-filter-universe">
                    <option value="">{t('filters.universe_all')}</option>
                    {universes.map((u) => <option key={u} value={u}>{t(`compliance:universes.${u}`, { defaultValue: u })}</option>)}
                </select>
                {notice && <span className="text-sm text-muted-foreground" data-testid="scenarios-notice">{notice}</span>}
            </div>
            {!isLoading && list.length === 0 && <EmptyState icon={mdiSitemapOutline} title={t('list.empty')} />}
            <ul className="divide-y divide-border rounded border border-border" data-testid="scenarios-list">
                {list.map((s) => (
                    <li key={s.uid} className="flex flex-wrap items-center gap-3 p-3" data-testid={`scenario-row-${s.code}`}>
                        <Checkbox
                            checked={s.enabled}
                            disabled={!canWrite || update.isPending}
                            onCheckedChange={(c) => update.mutate({ uid: s.uid, body: { enabled: c === true } })}
                            aria-label={t('actions.enable')}
                            data-testid={`scenario-toggle-${s.code}`}
                        />
                        <div className="min-w-0 flex-1">
                            <Link
                                to="/$org/remediation-scenarios/$code"
                                params={{ org, code: s.code }}
                                className="font-medium hover:underline"
                                data-testid={`scenario-open-${s.code}`}
                            >
                                {labelIn(s.label, i18n.language, s.code)}
                            </Link>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="outline" className="text-[10px]" data-testid={`scenario-origin-${s.code}`}>{t(`origin.${s.origin}`)}</Badge>
                                {s.replaced_by && (
                                    <Badge variant="outline" className="text-[10px]" data-testid={`scenario-replaced-${s.code}`}>{t('replaced_by', { code: s.replaced_by })}</Badge>
                                )}
                                {s.proposed_at && <Badge variant="outline" className="text-[10px]">{t('actions.proposed')}</Badge>}
                                <span>{triggerSummary(s)}</span>
                                <span>· {t('list.steps_count', { count: s.steps_count })}</span>
                                {s.running_count > 0 && <span>· {t('list.running', { count: s.running_count })}</span>}
                                {s.median_lead_seconds != null && <span>· {t('list.median', { duration: formatSeconds(s.median_lead_seconds, t) })}</span>}
                            </div>
                        </div>
                        {canWrite && (
                            <div className="flex flex-wrap gap-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => clone.mutate({ uid: s.uid }, {
                                        onSuccess: (c) => navigate({ to: '/$org/remediation-scenarios/$code', params: { org, code: c.code } }),
                                    })}
                                    data-testid={`scenario-clone-${s.code}`}
                                >
                                    <AppIcon path={mdiContentCopy} size="sm" />
                                    {t('actions.clone')}
                                </Button>
                                {s.origin === 'organization' && !s.proposed_at && (
                                    <Button size="sm" variant="outline" onClick={() => propose.mutate(s.uid)} data-testid={`scenario-propose-${s.code}`}>
                                        <AppIcon path={mdiSend} size="sm" />
                                        {t('actions.propose')}
                                    </Button>
                                )}
                                {s.enabled && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => backlog.mutate(s.uid, { onSuccess: (r) => setNotice(t('actions.start_backlog_done', { count: r.started })) })}
                                        data-testid={`scenario-start-backlog-${s.code}`}
                                    >
                                        <AppIcon path={mdiPlay} size="sm" />
                                        {t('actions.start_backlog')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}
